import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, TrendingUp, TrendingDown, DollarSign, BarChart3, 
  PieChart, Calendar, Star, Briefcase, RefreshCw,
  Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket, Wrench,
  Heart, Car, Bug, Leaf, Package, Monitor, Flame, Shield, Scissors, Home, Cog
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart as RechartsPieChart, Pie, Cell, Legend,
  AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0f172a', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

// Icon mapping
const iconMap = {
  Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket, Wrench,
  Heart, Car, Bug, Leaf, Package, Monitor, Flame, Shield, Scissors, Home, Cog
};

// Color mapping
const colorClasses = {
  blue: 'bg-blue-100 text-blue-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  teal: 'bg-teal-100 text-teal-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  lime: 'bg-lime-100 text-lime-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  amber: 'bg-amber-100 text-amber-600',
  orange: 'bg-orange-100 text-orange-600',
  red: 'bg-red-100 text-red-600',
  pink: 'bg-pink-100 text-pink-600',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-600',
  purple: 'bg-purple-100 text-purple-600',
  violet: 'bg-violet-100 text-violet-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  slate: 'bg-slate-100 text-slate-600',
};

export default function CategoryAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('90');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = `${API}/admin/reports/category-analytics?days=${period}`;
      if (selectedCategory !== 'all') {
        url += `&category_id=${selectedCategory}`;
      }
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch category analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [period, selectedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value) => `Php ${value?.toLocaleString() || 0}`;

  const getIcon = (iconName) => iconMap[iconName] || Wrench;
  const getColorClass = (colorName) => colorClasses[colorName] || colorClasses.slate;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex">
        <AdminSidebar />
        <AdminMobileNav />
        <main className="flex-1 p-4 md:p-8 md:ml-64 flex items-center justify-center pt-16 md:pt-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 p-4 md:p-8 md:ml-64 overflow-x-hidden pt-16 md:pt-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin/reports')} 
              className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Category Analytics</h1>
              <p className="text-slate-500">Performance insights and trends by service category</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 Days</SelectItem>
                <SelectItem value="30">30 Days</SelectItem>
                <SelectItem value="90">90 Days</SelectItem>
                <SelectItem value="180">6 Months</SelectItem>
                <SelectItem value="365">1 Year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <PieChart className="w-5 h-5 text-slate-600" />
              </div>
              <span className="text-slate-500 text-sm">Categories</span>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-testid="total-categories">
              {data?.summary?.total_categories || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-slate-500 text-sm">Total Bookings</span>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-testid="total-bookings">
              {data?.summary?.total_bookings?.toLocaleString() || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-slate-500 text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-testid="total-revenue">
              {formatCurrency(data?.summary?.total_revenue)}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-slate-500 text-sm">Avg Booking</span>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-testid="avg-booking">
              {formatCurrency(data?.summary?.avg_booking_value)}
            </p>
          </div>
        </div>

        {/* Category Performance Grid */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Category Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {data?.category_performance?.map((cat, idx) => {
              const IconComp = getIcon(cat.icon);
              const colorClass = getColorClass(cat.icon_color);
              return (
                <div 
                  key={cat.id} 
                  className="border border-stone-100 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? 'all' : cat.id)}
                  data-testid={`category-card-${cat.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 ${colorClass} rounded-xl flex items-center justify-center`}>
                      <IconComp className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">{cat.name}</h3>
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span className="text-xs text-slate-500">{cat.avg_rating || 0}</span>
                      </div>
                    </div>
                    {idx === 0 && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Top</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-slate-500">Bookings</p>
                      <p className="font-semibold text-slate-900">{cat.total_bookings}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Revenue</p>
                      <p className="font-semibold text-emerald-600">{formatCurrency(cat.total_revenue)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Trend Chart */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Revenue Trend</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.trend_data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }} 
                    tickFormatter={(val) => val.slice(5)}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }} 
                    tickFormatter={(val) => `Php ${(val/1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="total_revenue" 
                    stroke="#0f172a" 
                    fill="#0f172a" 
                    fillOpacity={0.1}
                    name="Revenue"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bookings Trend Chart */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Booking Volume</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.trend_data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }} 
                    tickFormatter={(val) => val.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar 
                    dataKey="total_bookings" 
                    fill="#3b82f6" 
                    radius={[4, 4, 0, 0]}
                    name="Bookings"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Second Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Distribution Pie */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Revenue Distribution</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={data?.category_performance?.slice(0, 8) || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="total_revenue"
                    nameKey="name"
                    label={({ name, percent }) => `${name.slice(0, 10)} ${(percent * 100).toFixed(0)}%`}
                  >
                    {data?.category_performance?.slice(0, 8).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Day of Week Analysis */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Demand by Day of Week</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.seasonal_data || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="day" type="category" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="bookings" fill="#10b981" radius={[0, 4, 4, 0]} name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top Services Table */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Top Performing Services</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Rank</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Service</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Category</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Bookings</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data?.top_services?.map((service, idx) => (
                  <tr key={service.id} className="border-b border-stone-50 hover:bg-stone-50">
                    <td className="py-3 px-4">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-amber-100 text-amber-700' : 
                        idx === 1 ? 'bg-slate-200 text-slate-700' : 
                        idx === 2 ? 'bg-orange-100 text-orange-700' : 'bg-stone-100 text-stone-600'
                      }`}>
                        {idx + 1}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-slate-900">{service.name}</td>
                    <td className="py-3 px-4 text-slate-500">{service.category}</td>
                    <td className="py-3 px-4 text-right text-slate-900">{service.bookings}</td>
                    <td className="py-3 px-4 text-right font-medium text-emerald-600">{formatCurrency(service.revenue)}</td>
                  </tr>
                ))}
                {(!data?.top_services || data.top_services.length === 0) && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-400">
                      No service data available for this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
